<template>
  <div>
    <box>
      <div slot="header">
        <span v-if="isOneAgent">
          {{ $l("components.agents.command", "Command") }}
          {{ title }}
        </span>

        <span v-else-if="isAllAgents">
          {{ $l("components.agents.commandAllAgents", "Command all agents") }}
        </span>

        <span v-else>
          {{
            $l("components.agents.commandGroupAgents", "Command group agents")
          }}
          {{ title }}
        </span>
      </div>

      <div style="margin: auto; width: 80%; min-height: 250px" class="mb-3">
        <div class="field">
          <label class="label">
            {{ $l("components.agents.commandType", "Command Type") }}
          </label>
          <div class="control">
            <v-select
              v-model="command.type"
              :clearable="false"
              :reduce="(option) => option.value"
              :options="[
                {
                  label: 'Scan',
                  value: 'scan',
                },
                {
                  label: 'Notify',
                  value: 'notify',
                },

                {
                  label: 'Execute',
                  value: 'execute',
                },

                {
                  label: 'Update',
                  value: 'update',
                },

                {
                  label: 'Quarantine',
                  value: 'quarantine',
                },

                {
                  label: 'Unquarantine',
                  value: 'unquarantine',
                },

                {
                  label: 'Update server information',
                  value: 'update_server_info',
                },

                {
                  label: 'Update license information',
                  value: 'update_license_info',
                },
              ]"
            />
          </div>
        </div>
        <div v-if="command.type === 'scan'" class="field">
          <label class="label"
            >{{ $l("components.agents.path", "Path") }}
          </label>
          <div class="control">
            <input
              v-model="command.payload.path"
              class="input"
              type="text"
              placeholder="D:\Download\abc.exe"
            />
          </div>
        </div>
        <div v-if="command.type === 'notify'" class="field">
          <label class="label"
            >{{ $l("components.agents.message", "Message") }}
          </label>
          <div class="control">
            <textarea
              v-model="command.payload.message"
              class="textarea"
              type="text"
            />
          </div>
        </div>
        <template v-if="command.type === 'execute'">
          <div class="field">
            <label class="label"
              >{{ $l("components.agents.executable", "Executable") }}
            </label>
            <div class="control">
              <div class="is-flex">
                <v-select
                  v-model="selectedExecutable"
                  append-to-body
                  style="flex-grow: 1"
                  :clearable="false"
                  label="name"
                  :options="listBinaries"
                />
                <button
                  class="button is-primary ml-3"
                  @click="showUploadModal = true"
                >
                  {{ $l("global.upload", "Upload") }}
                </button>
              </div>
              <p v-if="command.payload.md5" class="help">
                MD5: {{ command.payload.md5 }} - Please verify MD5 hash
                carefully before submit
              </p>
            </div>
          </div>
          <div class="field">
            <label class="label"
              >{{ $l("components.agents.args", "Arguments") }}
            </label>
            <div class="control">
              <input
                v-model="command.payload.arguments"
                class="input"
                type="text"
              />
            </div>
          </div>
          <div class="field">
            <label class="label"
              >{{ $l("components.agents.arch", "Architecture") }}
            </label>
            <div class="control">
              <v-select
                v-model="command.payload.architecture"
                append-to-body
                :clearable="false"
                :options="['win-x86', 'win-x64', 'win-any']"
              />
            </div>
          </div>
        </template>

        <div v-if="command.type === 'update_server_info'" class="field">
          <div class="control">
            <div class="columns is-gapless">
              <label class="label column"
                >{{ $l("components.agents.serverInfor", "Server information") }}
              </label>
              <div class="column is-narrow">
                <button
                  class="button is-primary is-small is-light"
                  @click="addServer"
                >
                  <octicon :icon="plus" />
                </button>
              </div>
            </div>

            <div
              v-for="(server, index) in command.payload.serverInfor"
              :key="index"
              class="control columns is-variable is-1 is-vcentered"
            >
              <div class="column">
                <v-select
                  v-model="server.protocol"
                  :clearable="false"
                  :reduce="(option) => option.value"
                  :options="[
                    {
                      label: 'https',
                      value: 'https',
                    },
                    {
                      label: 'http',
                      value: 'http',
                    },
                  ]"
                />
              </div>
              <div class="column">
                <input
                  v-model="server.address"
                  class="input"
                  type="text"
                  placeholder="IP address or domains"
                />
              </div>
              <div class="column">
                <input
                  v-model="server.port"
                  class="input"
                  type="text"
                  placeholder="Port"
                />
              </div>
              <div class="column">
                <input
                  v-model="server.license"
                  class="input"
                  type="text"
                  placeholder="License"
                />
              </div>
              <div class="column is-narrow">
                <button
                  class="button is-danger is-small is-light"
                  @click="command.payload.serverInfor.splice(index, 1)"
                >
                  <octicon :icon="x" />
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="command.type === 'update_license_info' && !this.isAllAgents"
          class="field"
        >
          <label class="label"
            >{{ $l("components.agents.currentLicense", "Current license") }}
          </label>
          <div class="control">
            <input
              v-model="command.payload.currentLicense"
              class="input"
              type="text"
              disabled
              :placeholder="
                $l('components.agents.currentLicense', 'Current license')
              "
            />
          </div>
        </div>

        <div v-if="command.type === 'update_license_info'" class="field">
          <label class="label"
            >{{ $l("components.agents.destLicense", "Dest license") }}
          </label>
          <div class="control">
            <input
              v-model="command.payload.destLicense"
              class="input"
              type="text"
              :placeholder="$l('components.agents.destLicense', 'Dest license')"
            />
          </div>
        </div>
      </div>

      <tabs
        v-if="pendingCommands && pendingCommands.length"
        :theme="$_ui_theme_tabs"
      >
        <tab
          :title="$l('components.agents.pendingCommands', 'Pending Commands')"
        >
          <data-table
            ref="commandsTable"
            :key="currentLocale"
            :items="pendingCommands"
            :per-page="5"
            :disable-filtering="true"
            :theme="$_ui_theme_tables"
          >
            <data-column
              field="type"
              :sortable="false"
              :label="$l('components.agents.commandType', 'Command Type')"
            >
              <template #default="{ item }">
                <div v-if="item.type === 'scan'">Scan</div>
                <div v-if="item.type === 'notify'">Notify</div>
                <div v-if="item.type === 'execute'">Execute</div>
                <div v-if="item.type === 'update'">Update</div>
                <div v-if="item.type === 'quarantine'">Quarantine</div>
                <div v-if="item.type === 'unquarantine'">Unquarantine</div>
                <div v-if="item.type === 'update_server_info'">
                  Update server information
                </div>
                <div v-if="item.type === 'update_license_info'">
                  Update license information
                </div>
              </template>
            </data-column>

            <data-column
              field="payload"
              :sortable="false"
              :label="$l('components.agents.payload', 'Payload')"
            >
              <template slot-scope="props">
                <div
                  v-for="(value, key) in props.item.payload"
                  :key="key"
                  class="tags has-addons mb-1 truncate"
                >
                  <Tooltip placement="top">
                    <template #content>
                      <div style="white-space: normal; min-width: 200px">
                        {{ value }}
                      </div>
                    </template>
                    <span class="is-primary mb-0">{{ key }}:</span>
                    <span class="is-info mb-0 ml-2">{{ value }}</span>
                  </Tooltip>
                </div>
              </template>
            </data-column>
            <data-column
              field="createdAt"
              :sortable="false"
              :render="formatDate"
              :label="$l('components.agents.time', 'Time')"
            />
          </data-table>
        </tab>
      </tabs>

      <div slot="footer">
        <div class="field is-grouped is-grouped-right">
          <div class="control">
            <button class="button is-info" @click.prevent="saveCommand">
              {{ $l("global.send", "Send") }}
            </button>
          </div>
          <div class="control">
            <button class="button is-primary" @click.prevent="$emit('close')">
              {{ $l("global.cancel", "Cancel") }}
            </button>
          </div>
        </div>
      </div>
    </box>

    <modal :show.sync="showUploadModal">
      <box>
        <div slot="header">
          {{ $l("components.agents.uploadBinary", "Upload Binary") }}
        </div>

        <div
          class="is-flex"
          style="justify-content: center; align-items: center"
        >
          <form id="binaryUpload">
            <div class="field">
              <div class="file is-boxed has-name">
                <label class="file-label">
                  <input
                    type="file"
                    class="file-input"
                    name="binary"
                    @change="onBinaryFileChange"
                  />
                  <span class="file-cta">
                    <span class="file-icon">
                      <octicon :scale="2" :icon="cloudUpload" />
                    </span>
                    <span class="file-label">
                      {{
                        $l(
                          "components.configurationUpdate.chooseAFile",
                          "Choose a file..."
                        )
                      }}
                    </span>
                  </span>
                  <span class="file-name">
                    {{
                      binaryFileName
                        ? binaryFileName
                        : $l(
                            "components.configurationUpdate.noFileChosen",
                            "No file chosen"
                          )
                    }}
                  </span>
                </label>
              </div>
            </div>
          </form>
        </div>

        <div slot="footer">
          <div class="field is-grouped is-grouped-right">
            <div class="control">
              <button class="button is-info" @click.prevent="uploadDb">
                {{ $l("global.upload", "Upload") }}
              </button>
            </div>
            <div class="control">
              <button
                class="button is-primary"
                @click.prevent="showUploadModal = false"
              >
                {{ $l("global.cancel", "Cancel") }}
              </button>
            </div>
          </div>
        </div>
      </box>
    </modal>
  </div>
</template>

<script>
import { DataTable, DataColumn, Modal, Tabs, Tab } from "@cyradar/ui";
import { plus, x } from "octicons-vue";
import AgentApi from "@/api/Agent";
import vSelect from "vue-select";
import Format from "date-fns/format";
import { mapGetters } from "vuex";
import { cloudUpload } from "octicons-vue";
import { Tooltip } from "iview";

export default {
  components: { vSelect, DataTable, DataColumn, Modal, Tabs, Tab, Tooltip },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    audience: {
      type: Array,
      required: true,
    },

    isAllAgents: {
      type: Boolean,
      default: false,
    },

    isOneAgent: {
      type: Boolean,
      default: false,
    },

    license: {
      type: String,
      default: "",
    },

    title: {
      type: String,
      default: "",
    },

    groupID: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      plus,
      x,
      command: {
        type: "scan",
        audience: [],
        payload: {
          path: "",
          message: "",
          executable: "",
          md5: "",
          arguments: "",
          architecture: "",
          serverInfor: [
            {
              protocol: "https",
              address: "",
              port: "",
              license: this.license ? this.license : "",
            },
          ],
          currentLicense: this.license ? this.license : "",
          destLicense: "",
        },
      },
      pendingCommands: [],
      showUploadModal: false,
      isLoading: false,
      binaryFileName: "",
      listBinaries: [],
      selectedExecutable: { name: "", md5: "" },
      cloudUpload,
    };
  },
  computed: {
    ...mapGetters(["currentLocale"]),
  },
  watch: {
    audience(val) {
      this.command = {
        type: "scan",
        audience: !this.isAllAgents ? val : ["*"],
        payload: {
          path: "",
          message: "",
          executable: "",
          md5: "",
          arguments: "",
          architecture: "",
          serverInfor: [
            {
              protocol: "https",
              address: "",
              port: "",
              license: this.license ? this.license : "",
            },
          ],
          currentLicense: this.license ? this.license : "",
          destLicense: "",
        },
      };
    },
    visible(val) {
      if (!val) {
        this.command = {
          type: "scan",
          audience: !this.isAllAgents ? val : ["*"],
          payload: {
            path: "",
            message: "",
            executable: "",
            md5: "",
            arguments: "",
            architecture: "",
            serverInfor: [
              {
                protocol: "https",
                address: "",
                port: "",
                license: "",
              },
            ],
            currentLicense: "",
            destLicense: "",
          },
        };

        return;
      }

      this.commandListFunc().then((data) => {
        this.pendingCommands = data.items;
      });
      this.fetchBinaries();
    },
    selectedExecutable(val) {
      this.command.payload.executable = this.selectedExecutable.name;
      this.command.payload.md5 = this.selectedExecutable.md5;
    },
  },
  methods: {
    fetchBinaries() {
      return AgentApi.getCommandBinaries(this.$http).then((data) => {
        this.listBinaries = data;
        this.selectedExecutable = { name: "", md5: "" };
      });
    },
    commandListFunc(filtering, sorting, paging) {
      return AgentApi.getCommandsVueTable(
        this.$http,
        filtering,
        sorting,
        paging,
        { audience: this.command.audience }
      ).then((res) => res.data);
    },
    saveCommand() {
      if (!this.command.type) {
        return;
      }

      let payload = null;
      if (this.command.type === "scan") {
        payload = {
          path: this.command.payload.path,
        };
      }

      if (this.command.type === "notify") {
        payload = {
          message: this.command.payload.message,
        };
      }

      if (this.command.type === "execute") {
        if (
          !this.command.payload.executable ||
          !this.command.payload.architecture
        ) {
          this.$store.dispatch("NOTIFY", {
            type: "warning",
            error: {
              message: `Please verify your input`,
            },
            duration: 5000,
          });
          return;
        }

        if (!this.command.payload.md5) {
          this.$store.dispatch("NOTIFY", {
            type: "warning",
            error: {
              message: `Something wrong`,
            },
            duration: 5000,
          });
          return;
        }

        payload = {
          executable: this.command.payload.executable,
          md5: this.command.payload.md5,
          arguments: this.command.payload.arguments,
          architecture: this.command.payload.architecture,
        };
      }

      if (this.command.type === "update_server_info") {
        payload = {
          serverinfo: this.command.payload.serverInfor,
        };
      }

      if (this.command.type === "update_license_info") {
        payload = {
          current_license: this.command.payload.currentLicense,
          dest_license: this.command.payload.destLicense,
        };
      }

      let content = {
        type: this.command.type,
        audience: !this.isAllAgents ? this.command.audience : ["*"],
        payload: payload,
      };

      if (!this.isAllAgents && !this.isOneAgent) {
        content = {
          ...content,
          groupID: this.groupID || null,
          audience: [],
        };
      }

      AgentApi.sendCommand(this.$http, content).then((data) => {
        this.$store.dispatch("NOTIFY", {
          type: "success",
          text: data.message,
          duration: 10000,
        });
        this.command = {
          type: "scan",
          audience: !this.isAllAgents ? this.audience : ["*"],
          payload: {
            path: "",
            message: "",
            executable: "",
            md5: "",
            arguments: "",
            architecture: "",
            serverInfor: [
              {
                protocol: "https",
                address: "",
                port: "",
                license: this.license ? this.license : "",
              },
            ],
            currentLicense: this.license ? this.license : "",
            destLicense: "",
          },
        };
      });
      this.$emit("close");
    },
    uploadDb() {
      let formData = new FormData(document.getElementById("binaryUpload"));

      this.isLoading = true;

      AgentApi.uploadCommandBinary(this.$http, formData)
        .then((res) => {
          this.$store.dispatch("NOTIFY", {
            type: "success",
            text: res.message || "Success",
            duration: 10000,
          });
        })
        .then(this.fetchBinaries)
        .finally((_) => {
          this.isLoading = false;
          this.showUploadModal = false;
        });
    },
    onBinaryFileChange(event) {
      let fileData = event.target.files[0];
      this.binaryFileName = fileData.name;
    },
    formatDate(val) {
      if (!val) {
        return "";
      }
      return Format(val, "YYYY-MM-DD HH:mm");
    },

    addServer() {
      this.command.payload.serverInfor.push({
        protocol: "https",
        address: "",
        port: "",
        license: this.license ? this.license : "",
      });
    },
  },
};
</script>

<style>
</style>
